import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { useAuthFacade } from '@/auth/facade/authFacade';
import { ROUTES } from '@myorg/type-lib';
import { logger } from '@myorg/type-lib';
import { STORAGE_KEYS } from '@/config/storageKeys';

// 1. Define the interface for your logs
interface BehaviorLogEntry {
  t: number;      // timestamp
  e: string;      // event type
  x?: number;     // x coordinate
  y?: number;     // y coordinate
  k?: string;     // key
}

export function useLogin() {
  const authStore = useAuthStore();
  const authFacade = useAuthFacade();
  const router = useRouter();

  const username = ref('');
  const password = ref('');
  const target_index = ref(0);
  const final_x = ref(0);
  const rememberMe = ref(true);

  const error = ref(authStore.error);
  const message = ref(authStore.message);

  // 2. Properly type the ref to avoid 'never' error
  const behaviorLog = ref<BehaviorLogEntry[]>([]);
  const sliderPath = ref<{ x: number, t: number }[]>([]);
  const lastEventTime = ref(Date.now());

  const record = (type: string, metadata: Partial<BehaviorLogEntry> = {}) => {
    const now = Date.now();
    behaviorLog.value.push({
      t: now - lastEventTime.value,
      e: type,
      ...metadata
    });
    if (behaviorLog.value.length > 100) behaviorLog.value.shift();
  };

  const doLogin = async () => {
    try {
      // 3. Include the behaviorLog in your login request
      const res = await authStore.handleLogin({
        username: username.value,
        password: password.value,
        rememberMe: rememberMe.value,
        behavioral_log: behaviorLog.value,
        slider_path: sliderPath.value,
        target_index: target_index.value,
        final_x: final_x.value
      });
      logger.log('Local Storage userRoles:', localStorage.getItem(STORAGE_KEYS.USER_ROLES)); // 檢查此時是否有值
      logger.log('res:', res.data.redirect_url); // 檢查此時是否有值
      
      // Use window.location.href for OAuth2 authorize redirects to ensure full page reload
      const redirectUrl = res.data.redirect_url;
      if (redirectUrl && redirectUrl.includes('/oauth2/authorize')) {
        window.location.href = redirectUrl;
      } else if (redirectUrl) {
        router.push(redirectUrl);
      } else {
        await authFacade.handleLoginSuccess();
        router.push(ROUTES.USER.PATH);
      }
    } catch (err) {
      logger.log('err', err);
      error.value = authStore.error;
      throw err
    }
  };

  const initCaptcha = async () => {
    try {
      // 3. Include the behaviorLog in your login request
      const res = await authStore.initCaptcha();
      return res
    } catch (err) {
      error.value = authStore.error;
    }
  };

  const doRegister = async () => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const res = await authStore.handleRegister({
        username: username.value,
        password: password.value,
        tz: userTimezone,
        behavioral_log: behaviorLog.value,
        slider_path: sliderPath.value,
        target_index: target_index.value,
        final_x: final_x.value,
        rememberMe: rememberMe.value,
      });
      if (!res?.data?.redirect_url) {
        await authFacade.handleLoginSuccess();
      }
      message.value = authStore.message;
      router.push(ROUTES.USER.PATH);
      error.value = '';
    } catch (err) {
      error.value = authStore.error;
      throw err
    }
  };

  // Event Handlers
  let throttleTimer = false;
  const handleMouseMove = (e: MouseEvent) => {
    if (throttleTimer) return;
    throttleTimer = true;
    setTimeout(() => {
      // Record relative to window or use getBoundingClientRect if you have a container ref
      record('mm', { x: e.clientX, y: e.clientY });
      throttleTimer = false;
    }, 100);
  };

  const handleKey = (e: KeyboardEvent) =>
    record(e.type === 'keydown' ? 'kd' : 'ku', { k: e.key });

  const handleClick = (e: MouseEvent) =>
    record('cl', { x: e.clientX, y: e.clientY });

  // Lifecycle hooks for Global recording
  onMounted(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', handleKey);
    window.addEventListener('click', handleClick);
  });

  onUnmounted(() => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('keydown', handleKey);
    window.removeEventListener('keyup', handleKey);
    window.removeEventListener('click', handleClick);
  });

  return {
    username,
    password,
    error,
    message,
    rememberMe,
    doLogin,
    doRegister,
    initCaptcha,
    behaviorLog,
    sliderPath,
    target_index,
    final_x,
    record // Export record so you can use it manually if needed
  };
}
